<template>
  <div class="api-test-panel">
    <el-card shadow="never">
      <div class="page-toolbar">
        <div>
          <div class="toolbar-title">DLL 功能自检 · 11 / 224 / 250</div>
          <p>在 {{ serverName }} 调用所选 DLL，校验握手、公钥、车型 ID 与支持版本。</p>
        </div>
      </div>
      <div class="toolbar-actions">
        <el-radio-group v-model="target" :disabled="running">
          <el-radio-button label="active">活动 DLL</el-radio-button>
          <el-radio-button label="candidate">候选 DLL</el-radio-button>
        </el-radio-group>
        <el-button
          v-for="item in FUNC_CASES"
          :key="item.func"
          type="primary"
          plain
          :loading="running && runningFunc === item.func"
          :disabled="running"
          @click="runOne(item.func)"
        >
          测试 {{ item.func }}
        </el-button>
        <el-button :disabled="running || !results.length" @click="resetResults">清空结果</el-button>
      </div>
      <el-alert class="test-note" type="info" :closable="false" show-icon>
        <template #title>每次只测试一个功能号，并且只在当前服务器执行。</template>
        不上传、提升或回滚 DLL，也不执行批处理。使用本机白名单 SN 和已有 iVer 的有效车型数据；真实业务调用会记录诊断日志。
        250 的授权码使用测试上下文 0，此页面不验证客户端加密和授权流程。
      </el-alert>
    </el-card>

    <el-card shadow="never">
      <template #header>
        <div class="card-header">
          <span>测试结果 <small v-if="lastRunAt">{{ runServer }} · {{ lastRunAt }}</small></span>
          <div class="toolbar-actions">
            <el-tag type="success">通过 {{ passCount }}</el-tag>
            <el-tag type="danger">失败 {{ failCount }}</el-tag>
          </div>
        </div>
      </template>
      <el-table :data="results" row-key="func" empty-text="请选择 11、224 或 250 单独测试">
        <el-table-column type="expand">
          <template #default="{ row }">
            <div class="result-details">
              <div v-if="row.dll">实际 DLL：{{ row.dll.target === 'candidate' ? '候选' : '活动' }} · {{ row.dll.version }}<br>{{ row.dll.artifact }}</div>
              <ul v-if="row.checks.length">
                <li v-for="check in row.checks" :key="check.name" :class="check.passed ? 'check-pass' : 'check-fail'">
                  {{ check.passed ? '✓' : '✗' }} {{ check.name }}
                </li>
              </ul>
              <details v-if="row.sample"><summary>测试样本</summary><pre>{{ pretty(row.sample) }}</pre></details>
              <details v-if="row.decoded" open><summary>解码结果</summary><pre>{{ pretty(row.decoded) }}</pre></details>
              <details v-if="row.response"><summary>原始响应</summary><pre>{{ pretty(row.response) }}</pre></details>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="测试项" min-width="240" />
        <el-table-column label="结果" width="90">
          <template #default="{ row }"><el-tag :type="statusTagType(row.status)">{{ statusText(row.status) }}</el-tag></template>
        </el-table-column>
        <el-table-column label="耗时" width="110">
          <template #default="{ row }">{{ row.durationMs === null ? '-' : `${row.durationMs} ms` }}</template>
        </el-table-column>
        <el-table-column prop="detail" label="说明（展开查看数据）" min-width="280" />
      </el-table>
    </el-card>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { FUNC_CASES, useMazdaApiTest } from '@/composables/mazda/useMazdaApiTest';
const props = defineProps({
  serverName: { type: String, default: '' },
  mazdaPost: { type: Function, required: true },
});
const { running, target, results, lastRunAt, runServer, passCount, failCount,
  runOne: executeOne, resetResults } = useMazdaApiTest(props.mazdaPost, () => props.serverName);
const runningFunc = ref('');
const runOne = async (func) => {
  runningFunc.value = func;
  try {
    await executeOne(func);
  } finally {
    runningFunc.value = '';
  }
};
const pretty = (value) => JSON.stringify(value, null, 2);
const statusTagType = (status) => ({ pass: 'success', fail: 'danger', running: 'warning', pending: 'info' }[status] || 'info');
const statusText = (status) => ({ pass: '通过', fail: '失败', running: '运行中', pending: '待运行' }[status] || status);
</script>

<style scoped>
.api-test-panel { display: flex; flex-direction: column; gap: 18px; }
.page-toolbar, .card-header { display: flex; justify-content: space-between; align-items: center; gap: 16px; flex-wrap: wrap; }
.toolbar-title { font-size: 18px; font-weight: 700; }
p, small { color: #667085; line-height: 1.7; }
.toolbar-actions { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
.toolbar-actions :deep(.el-button + .el-button) { margin-left: 0; }
.test-note { margin-top: 18px; }
.result-details { padding: 12px 24px; line-height: 1.8; overflow-wrap: anywhere; }
.result-details pre { white-space: pre-wrap; overflow-wrap: anywhere; background: #f5f7fa; padding: 12px; border-radius: 8px; }
.result-details summary { cursor: pointer; }
.check-pass { color: #24833d; }
.check-fail { color: #c93737; }
</style>
